import { useEffect, useRef, useState } from "react";

import { Settings as SettingsApi } from "../../../api";
import { ACTIONS, ENV } from "../../../utils";
import { usePermissions } from "../../../hooks";

import styles from "./Settings.module.scss";

function getImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  const value = String(imagePath);

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  return `${ENV.SERVER_HOST}/${value.replace(/\\/g, "/").replace(/^\/+/, "")}`;
}

export function SettingsPage() {
  const { hasAction } = usePermissions();
  const canEdit = hasAction("settings", ACTIONS.MOD);

  const settingsController = new SettingsApi();

  const [heroBannerPath, setHeroBannerPath] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [removeRequested, setRemoveRequested] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Leyenda pública de la tienda ("Estamos trabajando para servirte
  // mejor", etc.) -- independiente del banner de imagen de arriba,
  // aunque vive en el mismo registro de Configuración. savedMessage es
  // el texto tal como está guardado (para saber si hay cambios
  // pendientes sin comparar contra lo que la persona está escribiendo).
  const [bannerActive, setBannerActive] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [savedBannerActive, setSavedBannerActive] = useState(false);
  const [savedBannerMessage, setSavedBannerMessage] = useState("");
  const [isBannerSubmitting, setIsBannerSubmitting] = useState(false);
  const [bannerError, setBannerError] = useState("");
  const [bannerSuccessMessage, setBannerSuccessMessage] = useState("");

  const objectUrlRef = useRef("");

  useEffect(() => {
    async function loadSettings() {
      try {
        setIsLoading(true);
        setError("");

        const result = await settingsController.getSettings();
        const settings = result?.settings || result?.response || result || {};

        setHeroBannerPath(settings?.heroBanner?.path || "");

        const bannerActiveValue = Boolean(settings?.maintenanceBanner?.active);
        const bannerMessageValue =
          settings?.maintenanceBanner?.message ||
          "Estamos trabajando para servirte mejor.";

        setBannerActive(bannerActiveValue);
        setBannerMessage(bannerMessageValue);
        setSavedBannerActive(bannerActiveValue);
        setSavedBannerMessage(bannerMessageValue);
      } catch (loadError) {
        console.error("SettingsPage loadSettings error:", loadError);

        setError(
          loadError?.msg || "No fue posible cargar la configuración."
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  function handleFileChange(event) {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file) {
      return;
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    const nextPreviewUrl = URL.createObjectURL(file);

    objectUrlRef.current = nextPreviewUrl;

    setSelectedFile(file);
    setPreviewUrl(nextPreviewUrl);
    setRemoveRequested(false);
    setSuccessMessage("");
  }

  function handleCancelSelection() {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = "";
    }

    setSelectedFile(null);
    setPreviewUrl("");
  }

  function handleRemoveCurrentImage() {
    handleCancelSelection();
    setRemoveRequested(true);
    setSuccessMessage("");
  }

  async function handleSave() {
    if (!selectedFile && !removeRequested) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");
      setSuccessMessage("");

      const result = await settingsController.updateSettings({
        heroBanner: selectedFile,
        removeHeroBanner: removeRequested,
      });

      const settings = result?.settings || result?.response || result || {};

      setHeroBannerPath(settings?.heroBanner?.path || "");
      handleCancelSelection();
      setRemoveRequested(false);
      setSuccessMessage("Se guardó la imagen del banner.");
    } catch (saveError) {
      console.error("SettingsPage handleSave error:", saveError);

      setError(
        saveError?.msg || "No fue posible guardar la imagen del banner."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSaveBanner() {
    const trimmedMessage = bannerMessage.trim();

    if (bannerActive && !trimmedMessage) {
      setBannerError("Escribe el mensaje que quieres mostrar.");
      return;
    }

    try {
      setIsBannerSubmitting(true);
      setBannerError("");
      setBannerSuccessMessage("");

      const result = await settingsController.updateSettings({
        maintenanceBannerActive: bannerActive,
        maintenanceBannerMessage: trimmedMessage,
      });

      const settings = result?.settings || result?.response || result || {};

      const nextActive = Boolean(settings?.maintenanceBanner?.active);
      const nextMessage =
        settings?.maintenanceBanner?.message || trimmedMessage;

      setBannerActive(nextActive);
      setBannerMessage(nextMessage);
      setSavedBannerActive(nextActive);
      setSavedBannerMessage(nextMessage);
      setBannerSuccessMessage("Se guardó la leyenda de la tienda.");
    } catch (saveError) {
      console.error("SettingsPage handleSaveBanner error:", saveError);

      setBannerError(
        saveError?.msg || "No fue posible guardar la leyenda."
      );
    } finally {
      setIsBannerSubmitting(false);
    }
  }

  const hasPendingBannerChange =
    bannerActive !== savedBannerActive ||
    bannerMessage.trim() !== savedBannerMessage.trim();

  const displayedImageUrl = previewUrl || getImageUrl(heroBannerPath);
  const hasDisplayedImage = Boolean(displayedImageUrl) && !removeRequested;
  const hasPendingChange = Boolean(selectedFile) || removeRequested;

  return (
    <div className={styles.SettingsPage}>
      <section className={styles.Card}>
        <div className={styles.CardHeader}>
          <h2>Banner de Inicio</h2>

          <p>
            Esta es la foto grande que aparece en la página de Inicio de la
            tienda. Solo se puede cambiar la imagen -- el texto y el botón
            se mantienen igual.
          </p>
        </div>

        {error && (
          <div className={styles.ErrorMessage} role="alert">
            {error}
          </div>
        )}

        {successMessage && (
          <div className={styles.SuccessMessage} role="status">
            {successMessage}
          </div>
        )}

        {isLoading ? (
          <div className={styles.StateMessage}>Cargando...</div>
        ) : (
          <>
            <div className={styles.PreviewWrapper}>
              {hasDisplayedImage ? (
                <img
                  src={displayedImageUrl}
                  alt="Banner de Inicio"
                  className={styles.PreviewImage}
                />
              ) : (
                <div className={styles.PreviewPlaceholder}>
                  Sin imagen todavía
                </div>
              )}
            </div>

            {canEdit && (
              <>
                <div className={styles.Actions}>
                  <label
                    className={styles.UploadButton}
                    htmlFor="heroBanner"
                  >
                    {heroBannerPath || previewUrl
                      ? "Cambiar imagen"
                      : "Subir imagen"}
                  </label>

                  <input
                    id="heroBanner"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={isSubmitting}
                    hidden
                  />

                  {hasDisplayedImage && (
                    <button
                      type="button"
                      className={styles.RemoveButton}
                      onClick={handleRemoveCurrentImage}
                      disabled={isSubmitting}
                    >
                      Quitar imagen
                    </button>
                  )}

                  {selectedFile && (
                    <button
                      type="button"
                      className={styles.CancelButton}
                      onClick={handleCancelSelection}
                      disabled={isSubmitting}
                    >
                      Cancelar selección
                    </button>
                  )}
                </div>

                {removeRequested && (
                  <p className={styles.RemoveNotice}>
                    Se quitará la imagen actual al guardar.
                  </p>
                )}

                <button
                  type="button"
                  className={styles.SaveButton}
                  onClick={handleSave}
                  disabled={!hasPendingChange || isSubmitting}
                >
                  {isSubmitting ? "Guardando..." : "Guardar cambios"}
                </button>
              </>
            )}
          </>
        )}
      </section>

      <section className={styles.Card}>
        <div className={styles.CardHeader}>
          <h2>Modo mantenimiento</h2>

          <p>
            Al activarlo, la tienda pública deja de mostrar el catálogo,
            los productos y el carrito -- cualquier visitante ve
            únicamente este mensaje, como una tienda cerrada por
            mantenimiento. El panel de administración NO se ve afectado:
            tu equipo puede seguir entrando a{" "}
            <code>/login</code> y a <code>/admin</code> para trabajar y
            para desactivarlo cuando termines. Útil durante un traslado a
            producción o una actualización delicada.
          </p>
        </div>

        {bannerError && (
          <div className={styles.ErrorMessage} role="alert">
            {bannerError}
          </div>
        )}

        {bannerSuccessMessage && (
          <div className={styles.SuccessMessage} role="status">
            {bannerSuccessMessage}
          </div>
        )}

        {isLoading ? (
          <div className={styles.StateMessage}>Cargando...</div>
        ) : (
          <>
            <label className={styles.ToggleRow}>
              <span className={styles.ToggleSwitch}>
                <input
                  type="checkbox"
                  checked={bannerActive}
                  onChange={(event) => {
                    setBannerActive(event.target.checked);
                    setBannerSuccessMessage("");
                  }}
                  disabled={!canEdit || isBannerSubmitting}
                />

                <span />
              </span>

              <span className={styles.ToggleLabel}>
                {bannerActive ? "Leyenda activa" : "Leyenda desactivada"}
              </span>
            </label>

            <div className={styles.MessageField}>
              <label htmlFor="maintenanceBannerMessage">Mensaje</label>

              <textarea
                id="maintenanceBannerMessage"
                value={bannerMessage}
                onChange={(event) => {
                  setBannerMessage(event.target.value);
                  setBannerSuccessMessage("");
                }}
                placeholder="Ej. Estamos trabajando para servirte mejor."
                rows={3}
                maxLength={200}
                disabled={!canEdit || isBannerSubmitting}
              />

              <div className={styles.CharCount}>
                {bannerMessage.length}/200
              </div>
            </div>

            {canEdit && (
              <button
                type="button"
                className={styles.SaveButton}
                onClick={handleSaveBanner}
                disabled={!hasPendingBannerChange || isBannerSubmitting}
              >
                {isBannerSubmitting ? "Guardando..." : "Guardar cambios"}
              </button>
            )}
          </>
        )}
      </section>
    </div>
  );
}
